import type { IComponent } from "app/@types/ISwSettings";
import { EAvailabilityMode } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { categoryTextToIconFilter } from "app/filters/ngFilters";
import industryService from "app/pages/sales-intelligence/services/industryService";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { SalesSettings, SalesWorkspaceResources } from "app/pages/sales-intelligence/services/types";
import type { Industries } from "app/pages/sales-intelligence/types";
import { enrichCountriesWithComponentData } from "app/pages/sales-intelligence/utils/countries";
import type { ICountryObject, ICountryService } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import type { IRegionObject } from "app/services/RegionService";
import RegionService from "app/services/RegionService";

import { VISITS_FROM_COUNTRY_LOCAL_STORAGE_KEY } from "../../constants/common";

import type { AdvancedSearchSettingsService } from "./types";

const createSettingsService = (countryService: ICountryService): AdvancedSearchSettingsService => {
    const areCountriesAvailable = <T>(countries: readonly T[] | undefined): countries is Array<T> => {
        return Array.isArray(countries);
    };

    const getPersistedVisitsFromCountryIdWithFallback = (
        availableCountriesIds: ICountryObject["id"][],
    ): ICountryObject["id"] => {
        const countryFromLocalStorageParsed: ICountryObject["id"] | null = JSON.parse(
            localStorage.getItem(VISITS_FROM_COUNTRY_LOCAL_STORAGE_KEY) as string,
        );

        if (
            countryFromLocalStorageParsed === null ||
            !availableCountriesIds.includes(Number(countryFromLocalStorageParsed))
        ) {
            return availableCountriesIds[0];
        }

        return Number(countryFromLocalStorageParsed);
    };

    const getCurrentComponentIndustries = (industries: Industries) => {
        const industryTextToIcon = categoryTextToIconFilter();

        return industryService
            .getFlattenedIndustriesList(industries)
            .filter((category) => !category.isCustomCategory)
            .map((item) => ({
                ...item,
                icon: industryTextToIcon(item.text),
            }));
    };

    const getCompanyInformationCountries = () => {
        return countryService.countries.reduce<ICountryObject[]>((countries, country) => {
            if (country.id === 0 || country.id === WORLDWIDE_COUNTRY_ID) {
                return countries;
            }

            if (typeof country.children !== "undefined" && country.children.length > 0) {
                return countries.concat(country).concat(country.children);
            }

            return countries.concat(country);
        }, []);
    };

    const getAllowedCountries = (currentComponent: Readonly<IComponent>): readonly ICountryObject[] => {
        const allowedCountries: readonly ICountryObject[] | undefined =
            swSettings?.components?.LeadGenerationReport?.allowedCountries;

        const areAllowedCountriesAvailable = areCountriesAvailable(allowedCountries);
        if (!areAllowedCountriesAvailable) {
            return [];
        }

        return enrichCountriesWithComponentData(currentComponent, allowedCountries);
    };

    const getCurrentComponentCountries = (): ICountryObject[] => {
        const allowedCountries = getAllowedCountries(swSettings?.current);

        return allowedCountries.filter((country) => country.permitted);
    };

    const getCurrentComponentDesktopCountries = () => {
        const mobileWebCountriesIds: readonly number[] =
            swSettings?.components?.LeadGenerationReport?.resources?.MobileWebCountries ?? [];

        return getCurrentComponentCountries().filter((c) => !mobileWebCountriesIds.find((id) => c.id === id));
    };

    const getCurrentComponentRestrictedCountries = (): readonly ICountryObject[] => {
        const allowedCountries = getAllowedCountries(swSettings?.current);

        return allowedCountries.filter((country) => !country.permitted);
    };

    const getWorldwideCountryCodeOrFallback = (): number => {
        const availableUserCountries: number[] | undefined = swSettings?.current?.resources?.Countries;

        if (!areCountriesAvailable(availableUserCountries) || availableUserCountries.includes(WORLDWIDE_COUNTRY_ID)) {
            return WORLDWIDE_COUNTRY_ID;
        }

        return getPersistedVisitsFromCountryIdWithFallback(availableUserCountries);
    };

    const isAdvancedSearchFilterHidden = (filterKey: string) => {
        const hiddenAdvancedSearchFiltersKeys = Object.entries(
            swSettings?.components?.LeadGenerationReport?.resources ?? {},
        ).reduce<string[]>((hiddenKeys, [key, value]) => {
            if (value === EAvailabilityMode.HIDDEN) {
                return hiddenKeys.concat(key);
            }

            return hiddenKeys;
        }, []);
        return hiddenAdvancedSearchFiltersKeys.some((key) => key.match(new RegExp(filterKey, "i")));
    };

    const isSWResourceEnabled = (key: keyof SalesWorkspaceResources): boolean => {
        const SalesWorkspace = swSettings?.components?.SalesWorkspace;
        const { resources } = (SalesWorkspace as unknown as SalesSettings) ?? { resources: {} };

        return !!resources?.[key];
    };

    const getCountriesWithoutWW = (countries: ICountryObject[]): readonly ICountryObject[] => {
        return countries.filter(({ id }) => !CountryService.isWorldwideCountry(id));
    };

    const getRegions = (): readonly IRegionObject[] => {
        const availableRegions = salesSettingsHelper.getAvailableRegions();
        return RegionService.getRegions().map((c) => {
            return { ...c, permitted: availableRegions.includes(c.id) };
        });
    };

    return {
        isAdvancedSearchFilterHidden,
        isSWResourceEnabled,
        getWorldwideCountryCodeOrFallback,
        getCurrentComponentIndustries,
        getCompanyInformationCountries,
        getCurrentComponentCountries,
        getCurrentComponentRestrictedCountries,
        getCountriesWithoutWW,
        getRegions,
    };
};

export default createSettingsService(CountryService);
